import { useEffect, useState } from "react";
import Image from "next/image";
import { useSelector } from "react-redux";

import styles from "./TwoByTwoSummary.module.css";

import { RootState } from "@/redux/store";
import { DimensionSummaryData } from "@dimensional-engineering/dimensional-models";
import { ProfileColorMap } from "@/components/shared/ColorMap";

export default function TwoByTwoSummary(props: DimensionSummaryData) {
  const [quadrantFall, setQuadrantFall] = useState<number>(0);
  const imageURL = useSelector((state: RootState) => state.user.imageURL);
  const userState = useSelector((state: RootState) => state.user);

  useEffect(() => {
    const scoreX = props.twoByTwoGridData?.xAxisScore;
    const scoreY = props.twoByTwoGridData?.yAxisScore;
    if (scoreX && scoreY) {
      if (scoreX <= 0.5 && scoreY <= 0.5) {
        setQuadrantFall(4);
      } else if (scoreX > 0.5 && scoreY > 0.5) {
        setQuadrantFall(2);
      } else if (scoreX > 0.5 && scoreY <= 0.5) {
        setQuadrantFall(3);
      } else {
        setQuadrantFall(1);
      }
    }
  }, [props.twoByTwoGridData?.xAxisScore, props.twoByTwoGridData?.yAxisScore]);
  ///quadrant data are the labels-use xaxisscore and yaxisscore to highlight the correct quadrant
  ///and place the user image on there
  return (
    <div className={styles.wrapper}>
      <div className={styles.main}>
        <p className={styles.label}>
          {props.twoByTwoGridData?.yAxis.highDescriptor}
        </p>
        <div className={styles.parent}>
          <div className={styles.midCircle}></div>
          <div
            style={
              props.twoByTwoGridData
                ? {
                    bottom: `calc(${
                      props.twoByTwoGridData?.yAxisScore * 100
                    }% - 26px)`,
                    left: `calc(${
                      props.twoByTwoGridData?.xAxisScore * 100
                    }% - 26px)`,
                  }
                : {}
            }
            className={styles.scoreDiv}
          >
            <div
              style={
                userState.publicProfileData?.primaryNature?.nature
                  ? {
                      border: `3px solid ${
                        ProfileColorMap[
                          userState?.publicProfileData.primaryNature?.nature
                        ]
                      }`,
                    }
                  : {
                      border: `3px solid #55504F`,
                    }
              }
              className={styles.imageDiv}
            >
              <Image
                priority
                src={imageURL ? imageURL : "/default-image.png"}
                alt="Profile Image"
                fill
                style={{ borderRadius: "50%", border: "1px solid black" }}
              />
            </div>
          </div>
          {props.twoByTwoGridData?.quadrantData.map((value, index) => {
            return (
              <p className={`quadrant${index + 1}`} key={index}>
                {value}
              </p>
            );
          })}
          <div className={styles.rowTop}>
            <div
              style={
                quadrantFall === 1 ? { backgroundColor: " #fffff21f" } : {}
              }
              className={styles.leftTop}
            >
              <p className={styles.label}>
                {props.twoByTwoGridData?.xAxis.lowDescriptor}
              </p>
            </div>
            <div
              style={
                quadrantFall === 2 ? { backgroundColor: " #fffff21f" } : {}
              }
              className={styles.RightTop}
            >
              <p className={styles.label}>
                {props.twoByTwoGridData?.xAxis.highDescriptor}
              </p>
            </div>
          </div>
          <div className={styles.row}>
            <div
              style={
                quadrantFall === 4 ? { backgroundColor: " #fffff21f" } : {}
              }
              className={styles.leftBottom}
            ></div>
            <div
              style={
                quadrantFall === 3 ? { backgroundColor: " #fffff21f" } : {}
              }
              className={styles.rightBottom}
            ></div>
          </div>
        </div>
        <p className={styles.label}>
          {props.twoByTwoGridData?.yAxis.lowDescriptor}
        </p>
      </div>
    </div>
  );
}

export function TwoByTwoSummaryNormalized(props: DimensionSummaryData) {
  const imageURL = useSelector((state: RootState) => state.user.imageURL);
  const userState = useSelector((state: RootState) => state.user);
  const workArchetypeAcronym = userState.archetypesDiscovered?.find(
    (a) => a.archetype.archetypeGroupSlug === "work"
  )?.archetype.acronym;

  const scoreX = props.twoByTwoGridData?.xAxisScore;
  const scoreY = props.twoByTwoGridData?.yAxisScore;

  //normalizing data between 0.3 and 0.7
  const plotX = scoreX
    ? scoreX > 0.7
      ? 0.7 * 100
      : scoreX < 0.3
      ? 0.3 * 100
      : scoreX
    : scoreX;

  const plotY = scoreY
    ? scoreY > 0.7
      ? 0.7 * 100
      : scoreY < 0.3
      ? 0.3 * 100
      : scoreY
    : scoreY;

  return (
    <div className={styles.wrapper}>
      <div className={styles.main}>
        <p className={styles.label}>
          {props.twoByTwoGridData?.yAxis.highDescriptor}
        </p>
        <div className={styles.parent}>
          <div className={styles.midCircle}></div>
          <div
            style={
              plotY && plotX
                ? {
                    bottom: `calc(${plotY * 100 - 7.5}% - 26px)`,
                    left: `calc(${plotX * 100 - 7.5}% - 26px)`,
                  }
                : {}
            }
            className={styles.scoreDiv}
          >
            <div
              style={
                userState.publicProfileData?.primaryNature?.nature
                  ? {
                      border: `3px solid ${
                        ProfileColorMap[
                          userState?.publicProfileData.primaryNature?.nature
                        ]
                      }`,
                    }
                  : {
                      border: `3px solid #55504F`,
                    }
              }
              className={styles.imageDiv}
            >
              <Image
                priority
                src={imageURL ? imageURL : "/default-image.png"}
                alt="Profile Image"
                fill
                style={{ borderRadius: "50%", border: "1px solid black" }}
              />
            </div>
          </div>
          {props.twoByTwoGridData?.quadrantData.map((value, index) => {
            return (
              <p className={`quadrant${index + 1}`} key={index}>
                {value}
              </p>
            );
          })}
          <div className={styles.rowTop}>
            <div
              style={
                workArchetypeAcronym === "ID"
                  ? {
                      background: `linear-gradient(to top right,transparent 50%, rgb(255 255 242 / 15%)50%)`,
                    }
                  : workArchetypeAcronym === "DI"
                  ? {
                      background: `linear-gradient(to bottom left,transparent 50%, rgb(255 255 242 / 15%)50%)`,
                    }
                  : {}
              }
              className={styles.leftTop}
            >
              <p className={styles.label}>
                {props.twoByTwoGridData?.xAxis.lowDescriptor}
              </p>
            </div>
            <div
              style={
                workArchetypeAcronym === "IS"
                  ? {
                      background: `linear-gradient(to top left,transparent 50%,rgb(255 255 242 / 15%) 50%)`,
                    }
                  : workArchetypeAcronym === "SI"
                  ? {
                      background: `linear-gradient(to bottom right,transparent 50%,rgb(255 255 242 / 15%) 50%)`,
                    }
                  : {}
              }
              className={styles.RightTop}
            >
              <p className={styles.label}>
                {props.twoByTwoGridData?.xAxis.highDescriptor}
              </p>
            </div>
          </div>
          <div className={styles.row}>
            <div
              style={
                workArchetypeAcronym === "DC"
                  ? {
                      background: `linear-gradient(to top left,transparent 50%,rgb(255 255 242 / 15%) 50%)`,
                    }
                  : workArchetypeAcronym === "CD"
                  ? {
                      background: `linear-gradient(to bottom right,transparent 50%,rgb(255 255 242 / 15%) 50%)`,
                    }
                  : {}
              }
              className={styles.leftBottom}
            ></div>
            <div
              style={
                workArchetypeAcronym === "SC"
                  ? {
                      background: `linear-gradient(to top right,transparent 50%,rgb(255 255 242 / 15%) 50%)`,
                    }
                  : workArchetypeAcronym === "CS"
                  ? {
                      background: `linear-gradient(to bottom left,transparent 50%,rgb(255 255 242 / 15%) 50%)`,
                    }
                  : {}
              }
              className={styles.rightBottom}
            ></div>
          </div>
        </div>
        <p className={styles.label}>
          {props.twoByTwoGridData?.yAxis.lowDescriptor}
        </p>
        <div className={styles.lineTopLeft}></div>
        <div className={styles.lineTopRight}></div>
        <div className={styles.lineBottomLeft}></div>
        <div className={styles.lineBottomRight}></div>
        <p className={styles.di}>(DI)</p>
        <p className={styles.id}>(ID)</p>
        <p className={styles.is}>(IS)</p>
        <p className={styles.si}>(SI)</p>
        <p className={styles.sc}>(SC)</p>
        <p className={styles.cs}>(CS)</p>
        <p className={styles.cd}>(CD)</p>
        <p className={styles.dc}>(DC)</p>
      </div>
    </div>
  );
}
